import React from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';
import { parseStatusColorConfig } from '../../utils/statusColor';

export interface Lead {
  leadId: number;
  clientId: number;
  leadAssignedTo?: number;
  leadStatusId?: number;
  leadDescr?: string;
  leadContactedDate?: string;
  leadNotes?: string;
  leadValue?: number;
  leadProfit?: number;
  leadCreatedAt?: string;
  leadCreatedBy?: number;
  leadUpdatedAt?: string;
  leadUpdatedBy?: number;
  // Related data from backend
  client?: {
    clientId: number;
    clientName?: string;
  };
  leadAssignedToUser?: {
    id: number;
    userFirstName?: string;
    userDisplayName?: string;
  };
  leadStatusDescr?: {
    id: number;
    as_descr?: string;
    as_color?: string;
  };
  leadCreatedUser?: {
    userFirstName?: string;
  };
  leadUpdatedUser?: {
    userFirstName?: string;
  };
  FileAttachment?: Array<{
    id: number;
    fileName?: string;
    filePath?: string;
    fileType?: string;
    fileSize?: number;
    uploadedAt?: string;
  }>;
}

export interface LeadsResponse {
  records: Lead[];
  openedCount?: number;
  closedCount?: number;
  canceledCount?: number;
}

export const useLeads = (searchParams?: string, filter?: string) => {
  let queryString = '';
  if (searchParams || filter) {
    const params = new URLSearchParams();
    if (searchParams) params.append('searchParams', searchParams);
    if (filter) params.append('filter', filter);
    queryString = `?${params.toString()}`;
  }
  
  const { data, error, isLoading, mutate } = useSWR<LeadsResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  // Extract records array from response
  const leads = data?.records || [];

  return {
    data: leads,
    leads: leads,
    total: leads.length,
    isLoading,
    isError: error,
    mutate,
    openedCount: data?.openedCount || 0,
    closedCount: data?.closedCount || 0,
    canceledCount: data?.canceledCount || 0,
  };
};

export const useLead = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Lead>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    lead: data,
    data: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();
  
  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get users for select dropdown
export const useUsersForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/users',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    users: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((user) => ({
      value: String(user.id),
      label: user.userDisplayName || `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() || `User #${user.id}`,
    })),
  };
};

// Hook to get lead statuses for select dropdown
export const useLeadStatusesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-app-status',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    statuses: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((status) => ({
      value: String(status.id),
      label: status.as_descr || `Status #${status.id}`,
      icon: React.createElement('span', {
        className: 'inline-block h-2.5 w-2.5 rounded-full border border-black/10',
        style: { backgroundColor: parseStatusColorConfig(status.as_color).backgroundColor },
      }),
    })),
  };
};

